// Configurator wizard — PROMPT 3.3M Defects I + J.
// Reads facets exclusively from lib/catalog/facets.js. Q4 budget config
// from data/quiz.json. On mobile Q2/Q3 use native <select>. Results include
// a live "Add Full Loadout to Cart" flow with IDLE / ADDING / OK /
// PARTIAL_OK / FAIL states.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HOME } from "../constants/testIds";
import { BUSINESS } from "../constants/business";
import { loadQuizCategories } from "../lib/quiz/categories";
import { canonicalizeTagValue } from "../lib/catalog/normalize";
import { useCart } from "../state/cart";
import { useToast } from "../components/loadout/Toast";
import quizConfig from "../data/quiz.json";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function isMobile() {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 768px)").matches;
}

// Determine variantId to add. Prefer product.variantId or product.variants[0].id
// (already present from /api/quiz/recommend when the backend enriches them);
// otherwise fetch /api/shopify/product/<handle> which returns variants as a
// Shopify Storefront GraphQL connection: { variants: { edges: [{ node: {id} }] } }.
async function resolveVariantId(product) {
        if (product.variantId) return product.variantId;
        if (product.variants && Array.isArray(product.variants) && product.variants[0]) {
                return product.variants[0].id;
        }
        try {
                const r = await fetch(`${BACKEND}/api/shopify/product/${product.handle}`);
                if (!r.ok) return null;
                const j = await r.json();
                // Normalise both possible shapes:
                //   REST-style: j.variants = [{id, ...}, ...]
                //   GraphQL:     j.variants = { edges: [{ node: {id, ...} }, ...] }
                if (Array.isArray(j.variants) && j.variants[0]) {
                        return j.variants[0].id || null;
                }
                const edges = (j.variants && j.variants.edges) || [];
                const node = edges[0] && edges[0].node;
                if (node && node.id) return node.id;
                return null;
        } catch (_e) {
                return null;
        }
}

export default function QuizModal({ open, onClose }) {
        const cart = useCart();
        const [stepIndex, setStepIndex] = useState(0);
        const [category, setCategory] = useState(null); // Q1 single-select
        const [subAttr1, setSubAttr1] = useState({}); // Q2 { facetKey: value }
        const [subAttr2, setSubAttr2] = useState({}); // Q3 { facetKey: value }
        const [handSize, setHandSize] = useState("");   // Q_HAND { "small"|"medium"|"large"|"" skipped }
        const [budget, setBudget] = useState(5000);
        const [results, setResults] = useState(null);
        const [loadingResults, setLoadingResults] = useState(false);
        // Cart-add state machine
        const [addState, setAddState] = useState("idle"); // idle|adding|ok|partial|fail
        const [addResult, setAddResult] = useState(null);
        const triggerRef = useRef(null);
        const [shareToast, showShareToast] = useToast();

        // Builds the deterministic share URL and copies it to the clipboard.
        // NOTE: /loadouts/shared landing page is a later phase — today the
        // button only generates + copies the link.
        const handleShareLoadout = async () => {
                const handles = (results?.results || []).map((r) => r.handle).filter(Boolean);
                if (!handles.length) return;
                const url = `${window.location.origin}/loadouts/shared?items=${handles.join(",")}`;
                try {
                        await navigator.clipboard.writeText(url);
                        showShareToast("Link copied", { kind: "success" });
                } catch (_e) {
                        const ta = document.createElement("textarea");
                        ta.value = url;
                        ta.style.position = "fixed";
                        ta.style.opacity = "0";
                        document.body.appendChild(ta);
                        ta.select();
                        try {
                                document.execCommand("copy");
                                showShareToast("Link copied", { kind: "success" });
                        } catch (_e2) {
                                window.prompt("Copy your loadout link:", url);
                        }
                        document.body.removeChild(ta);
                }
        };

        // Load quiz categories via SSOT (throws QuizCategoriesEmptyError if empty).
        const categories = useMemo(() => {
                try { return loadQuizCategories(); } catch (_e) { return []; }
        }, []);

        // Live options fetched from /api/catalog/options?category=<product_type>.
        // Zero hardcoded option literals — the two facets we ask about (Q2/Q3)
        // are the first two keys of the live-derived options dict.
        const [liveOptions, setLiveOptions] = useState({}); // { key: [values] }
        useEffect(() => {
                if (!category) return;
                const cat = categories.find((c) => c.id === category);
                if (!cat) return;
                (async () => {
                        try {
                                const r = await fetch(`${BACKEND}/api/catalog/options?category=${encodeURIComponent(cat.productType)}`);
                                if (!r.ok) return;
                                const j = await r.json();
                                setLiveOptions(j.options || {});
                        } catch (_e) {}
                })();
        }, [category, categories]);
        const chosenFacets = useMemo(() => {
                // Hand-size is promoted to its own dedicated step and must not
                // be reused as Q2/Q3.
                const keys = Object.keys(liveOptions).filter((k) => k !== "size");
                return keys.slice(0, 2).map((k) => [k, liveOptions[k]]);
        }, [liveOptions]);

        // Hand-size options come from the live `size` facet when the current
        // category actually tags products with it (today: mice).
        const handSizeOptions = useMemo(() => {
                const vals = liveOptions.size || [];
                // Canonical order: small -> medium -> large; drop anything else.
                // Fingertip-tagged mice count as "small" (backend matches both).
                const want = ["small", "medium", "large"];
                return want.filter((v) => vals.includes(v) || (v === "small" && vals.includes("fingertip")));
        }, [liveOptions]);
        const hasHandSizeStep = handSizeOptions.length > 0;

        const totalSteps =
                3 +
                (chosenFacets.length >= 2 ? 1 : 0) +
                (hasHandSizeStep ? 1 : 0) +
                1; // Q1..Qn + results
        const stepQ = (idx) => {
                if (idx === 0) return "q1_category";
                if (idx === 1) return "q2";
                if (chosenFacets.length >= 2) {
                        if (idx === 2) return "q3";
                        if (idx === 3 && hasHandSizeStep) return "q_hand_size";
                        if (idx === 3) return "q4_budget";
                        if (idx === 4) return "q4_budget";
                        return "results";
                }
                // chosenFacets < 2 (no q3 step)
                if (idx === 2 && hasHandSizeStep) return "q_hand_size";
                if (idx === 2) return "q4_budget";
                if (idx === 3) return "q4_budget";
                return "results";
        };
        const currentStep = stepQ(stepIndex);
        const isResults = currentStep === "results";

        const restart = useCallback(() => {
                setStepIndex(0);
                setCategory(null);
                setSubAttr1({});
                setSubAttr2({});
                setHandSize("");
                setBudget(5000);
                setResults(null);
                setAddState("idle");
                setAddResult(null);
        }, []);

        useEffect(() => {
                if (!open) return;
                triggerRef.current = document.activeElement;
                const onKey = (e) => {
                        if (e.key === "Escape") { e.preventDefault(); onClose(); }
                };
                document.addEventListener("keydown", onKey);
                return () => {
                        document.removeEventListener("keydown", onKey);
                        if (triggerRef.current?.focus) triggerRef.current.focus();
                };
        }, [open, onClose]);

        const runRecommend = useCallback(async () => {
                setLoadingResults(true);
                try {
                        const cat = categories.find((c) => c.id === category);
                        // Convert sub-attr picks into { key, value } list. All go through
                        // canonicalizeTagValue so the backend sees byte-identical shapes.
                        const choices = [];
                        for (const [k, v] of Object.entries({ ...subAttr1, ...subAttr2 })) {
                                if (v) choices.push({ key: k, value: canonicalizeTagValue(v) });
                        }
                        if (handSize) {
                                choices.push({ key: "size", value: canonicalizeTagValue(handSize) });
                        }
                        const r = await fetch(`${BACKEND}/api/quiz/resolve`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                        category: cat ? cat.productType : category,
                                        choices,
                                        budget,
                                }),
                        });
                        const j = await r.json();
                        setResults(j);
                } catch (_e) {
                        setResults({ results: [], totalCount: 0, tier: null });
                } finally {
                        setLoadingResults(false);
                }
        }, [categories, category, subAttr1, subAttr2, handSize, budget]);

        useEffect(() => {
                if (isResults && !results && !loadingResults) runRecommend();
        }, [isResults, results, loadingResults, runRecommend]);

        // --- Cart-add flow (per-product) -----------------------------------
        // Add a single result to the cart (primary path — one product at a time).
        const [addingHandle, setAddingHandle] = useState(null);
        const [addedHandles, setAddedHandles] = useState(new Set());
        const handleAddSingleToCart = async (p) => {
                setAddingHandle(p.handle);
                setAddResult(null);
                setAddState("idle");
                try {
                        const variantId = await resolveVariantId(p);
                        if (!variantId) {
                                setAddState("fail");
                                setAddResult({ successes: [], failures: [{ title: p.title, reason: "no_variant" }] });
                                return;
                        }
                        const r = await cart.addLine(variantId, { intent: "buy", addedFrom: "quiz.single.v1" });
                        if (r && r.ok) {
                                setAddedHandles((prev) => new Set(prev).add(p.handle));
                                setAddState("ok");
                        } else {
                                setAddState("fail");
                                setAddResult({ successes: [], failures: [{ title: p.title, reason: (r && r.detail) || "add_failed" }] });
                        }
                } catch (_e) {
                        setAddState("fail");
                        setAddResult({ successes: [], failures: [{ title: p.title, reason: "network" }] });
                } finally {
                        setAddingHandle(null);
                }
        };

        const handleAddFullLoadoutToCart = async () => {
                if (!results || !results.results) return;
                setAddState("adding");
                setAddResult(null);
                const successes = [];
                const failures = [];
                for (const p of results.results) {
                        const variantId = await resolveVariantId(p);
                        if (!variantId) {
                                failures.push({ title: p.title, reason: "no_variant" });
                                continue;
                        }
                        try {
                                const r = await cart.addLine(variantId, {
                                        intent: "buy",
                                        addedFrom: "quiz.full_loadout.v1",
                                });
                                if (r && r.ok) successes.push({ title: p.title });
                                else failures.push({ title: p.title, reason: r && r.detail ? r.detail : "add_failed" });
                        } catch (_e) {
                                failures.push({ title: p.title, reason: "network" });
                        }
                }
                setAddResult({ successes, failures });
                if (failures.length === 0) setAddState("ok");
                else if (successes.length === 0) setAddState("fail");
                else setAddState("partial");
        };

        if (!open) return null;

        const canGoNext = () => {
                if (currentStep === "q1_category") return !!category;
                return true;
        };

        return (
                <div
                        data-testid={HOME.quizModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="quiz-modal-title"
                        className="fixed inset-0 z-50 bg-black/80 flex items-stretch md:items-center justify-center p-0 md:p-6"
                        onClick={onClose}
                        style={{ height: "100dvh" }}
                >
                        <div
                                className="relative w-full md:max-w-[900px] bg-[#0A0A0A] border-white/15 md:border max-h-[100dvh] md:max-h-[90dvh] flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                        >
                                {/* Top progress dots (mobile) */}
                                <div
                                        className="sticky-top-safe md:hidden flex items-center justify-center gap-2 pt-3 pb-2 bg-[#0A0A0A] border-b border-white/10"
                                >
                                        {Array.from({ length: totalSteps }).map((_, i) => (
                                                <span
                                                        key={i}
                                                        className="block w-2 h-2 rounded-full"
                                                        style={{
                                                                background:
                                                                        i === stepIndex ? "#FF9F1C"
                                                                        : i < stepIndex ? "rgba(255,255,255,0.5)"
                                                                        : "rgba(255,255,255,0.2)",
                                                        }}
                                                />
                                        ))}
                                </div>

                                <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-white/10">
                                        <div>
                                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C] uppercase">
                                                        Configurator · Step {stepIndex + 1} / {totalSteps}
                                                </p>
                                                <h2
                                                        id="quiz-modal-title"
                                                        className="mt-2 font-display text-[26px] md:text-[32px] uppercase leading-tight text-white"
                                                >
                                                        {currentStep === "q1_category" && "What are you rigging?"}
                                                        {currentStep === "q2" && `Refine your ${category}`}
                                                        {currentStep === "q3" && `More about your ${category}`}
                                                        {currentStep === "q_hand_size" && "What's your hand size?"}
                                                        {currentStep === "q4_budget" && "What's your budget?"}
                                                        {isResults && "Your Loadout"}
                                                </h2>
                                        </div>
                                        <button
                                                type="button"
                                                data-testid={HOME.quizClose}
                                                onClick={onClose}
                                                aria-label="Close configurator"
                                                className="touch-target w-12 h-12 flex items-center justify-center text-white/60 hover:text-white font-mono text-[18px] leading-none"
                                        >
                                                ✕
                                        </button>
                                </div>

                                <div className="p-6 flex-1 overflow-y-auto">
                                        {/* Q1 — Category (single-select radio) */}
                                        {currentStep === "q1_category" && (
                                                <div
                                                        data-testid={HOME.quizCategoryChips}
                                                        role="radiogroup"
                                                        aria-label="Choose category"
                                                        className="flex flex-col gap-3"
                                                >
                                                        {categories.map((c) => {
                                                                const selected = category === c.id;
                                                                return (
                                                                        <button
                                                                                key={c.id}
                                                                                type="button"
                                                                                role="radio"
                                                                                aria-checked={selected}
                                                                                data-testid={`quiz-chip-${c.id}`}
                                                                                onClick={() => setCategory(c.id)}
                                                                                className={
                                                                                        "touch-target w-full text-left px-5 py-4 border transition-colors " +
                                                                                        (selected
                                                                                                ? "border-[#FF9F1C] bg-[#FF9F1C]/10 text-[#FF9F1C]"
                                                                                                : "border-white/25 text-white hover:border-white")
                                                                                }
                                                                                style={{ minHeight: 56 }}
                                                                        >
                                                                                <span className="font-display text-[18px] uppercase leading-none">{c.title}</span>
                                                                                <p className="mt-1 font-mono text-[11px] tracking-[0.14em] text-white/60 uppercase">
                                                                                        {c.subline}
                                                                                </p>
                                                                        </button>
                                                                );
                                                        })}
                                                </div>
                                        )}

                                        {/* Q2 / Q3 — single-select facet dropdown (native on mobile) */}
                                        {(currentStep === "q2" || currentStep === "q3") && chosenFacets.length > 0 && (
                                                (() => {
                                                        const idx = currentStep === "q2" ? 0 : 1;
                                                        const [facetKey, options] = chosenFacets[idx];
                                                        const stateSetter = idx === 0 ? setSubAttr1 : setSubAttr2;
                                                        const stateVal = (idx === 0 ? subAttr1 : subAttr2)[facetKey] || "";
                                                        return (
                                                                <div>
                                                                        <label className="block font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">
                                                                                {facetKey}
                                                                        </label>
                                                                        <select
                                                                                data-testid={`quiz-select-${facetKey}`}
                                                                                value={stateVal}
                                                                                onChange={(e) => stateSetter({ [facetKey]: e.target.value })}
                                                                                className="mt-3 w-full bg-[#111] border border-white/25 text-white px-4 py-3 focus:border-[#FF9F1C] focus:outline-none"
                                                                                style={{ minHeight: 56, fontSize: 16 }}
                                                                        >
                                                                                <option value="">Any</option>
                                                                                {options.map((opt) => (
                                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                                ))}
                                                                        </select>
                                                                        <p className="mt-3 font-sans text-white/60 text-[13px] md:text-[14px]">
                                                                                Optional — pick "Any" to skip.
                                                                        </p>
                                                                </div>
                                                        );
                                                })()
                                        )}

                                        {/* Q — Hand size (uses the existing `size` tag: small|medium|large) */}
                                        {currentStep === "q_hand_size" && (
                                                <div data-testid="quiz-hand-size">
                                                        <label className="block font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">
                                                                Hand size
                                                        </label>
                                                        <div
                                                                role="radiogroup"
                                                                aria-label="Choose hand size"
                                                                className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
                                                        >
                                                                {handSizeOptions.map((v) => {
                                                                        const selected = handSize === v;
                                                                        return (
                                                                                <button
                                                                                        key={v}
                                                                                        type="button"
                                                                                        role="radio"
                                                                                        aria-checked={selected}
                                                                                        data-testid={`quiz-hand-size-${v}`}
                                                                                        onClick={() => setHandSize(selected ? "" : v)}
                                                                                        className={
                                                                                                "touch-target w-full text-left px-5 py-4 border transition-colors " +
                                                                                                (selected
                                                                                                        ? "border-[#FF9F1C] bg-[#FF9F1C]/10 text-[#FF9F1C]"
                                                                                                        : "border-white/25 text-white hover:border-white")
                                                                                        }
                                                                                        style={{ minHeight: 56 }}
                                                                                >
                                                                                        <span className="font-display text-[18px] uppercase leading-none">{v}</span>
                                                                                </button>
                                                                        );
                                                                })}
                                                        </div>
                                                        <div className="mt-4 flex items-center gap-3">
                                                                <button
                                                                        type="button"
                                                                        data-testid="quiz-hand-size-skip"
                                                                        onClick={() => { setHandSize(""); setStepIndex(stepIndex + 1); }}
                                                                        className="touch-target font-mono text-[11px] tracking-[0.18em] uppercase text-white/60 underline underline-offset-4 hover:text-white"
                                                                        style={{ minHeight: 44 }}
                                                                >
                                                                        Skip
                                                                </button>
                                                                <p className="font-sans text-white/60 text-[13px]">
                                                                        Optional — Loadout only ranks mice tagged with this hand size.
                                                                </p>
                                                        </div>
                                                </div>
                                        )}

                                        {/* Q4 — Budget */}
                                        {currentStep === "q4_budget" && (
                                                <div>
                                                        <div className="flex items-center gap-3">
                                                                <button
                                                                        type="button"
                                                                        aria-label="Decrease budget"
                                                                        onClick={() => setBudget(Math.max(quizConfig.budget_min, budget - quizConfig.budget_step))}
                                                                        className="touch-target w-12 h-12 border border-white/25 text-white text-2xl"
                                                                >
                                                                        −
                                                                </button>
                                                                <input
                                                                        data-testid={HOME.quizBudgetSlider}
                                                                        type="number"
                                                                        inputMode="numeric"
                                                                        pattern="[0-9]*"
                                                                        value={budget}
                                                                        min={quizConfig.budget_min}
                                                                        max={quizConfig.budget_max}
                                                                        step={quizConfig.budget_step}
                                                                        onChange={(e) => setBudget(Math.max(quizConfig.budget_min, Math.min(quizConfig.budget_max, parseInt(e.target.value || "0", 10))))}
                                                                        className="flex-1 bg-transparent text-center font-display text-[28px] md:text-[36px] text-[#FF9F1C] border-b border-white/25 focus:outline-none focus:border-[#FF9F1C]"
                                                                        style={{ minHeight: 56 }}
                                                                />
                                                                <button
                                                                        type="button"
                                                                        aria-label="Increase budget"
                                                                        onClick={() => setBudget(Math.min(quizConfig.budget_max, budget + quizConfig.budget_step))}
                                                                        className="touch-target w-12 h-12 border border-white/25 text-white text-2xl"
                                                                >
                                                                        +
                                                                </button>
                                                        </div>
                                                        <p
                                                                data-testid={HOME.quizBudgetValue}
                                                                className="mt-3 text-center font-mono text-[12px] tracking-[0.16em] text-white/70 uppercase"
                                                        >
                                                                Cap: ₹{budget.toLocaleString("en-IN")}
                                                        </p>
                                                </div>
                                        )}

                                        {/* Results */}
                                        {isResults && (
                                                <div>
                                                        {loadingResults && (
                                                                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/40">
                                                                        Calculating matches…
                                                                </p>
                                                        )}
                                                        {!loadingResults && results && (
                                                                (() => {
                                                                        const tier = results.tier;
                                                                        const dropped = results.dropped_keys || [];
                                                                        let chipClass = "";
                                                                        let chipText = "";
                                                                        if (tier === 0) { chipClass = "border-emerald-500 text-emerald-400 bg-emerald-500/10"; chipText = "Exact match"; }
                                                                        else if (tier === 1) { chipClass = "border-[#FF9F1C] text-[#FF9F1C] bg-[#FF9F1C]/10"; chipText = `Showing broader matches — no exact match for ${dropped.join(", ")}.`; }
                                                                        else if (tier === 2) { chipClass = "border-[#FF9F1C] text-[#FF9F1C] bg-[#FF9F1C]/10"; chipText = "Broader matches"; }
                                                                        else if (tier === 3) { chipClass = "border-white/40 text-white/70 bg-white/5"; chipText = `Showing best matches in ${category}`; }
                                                                        if (!chipText) return null;
                                                                        return (
                                                                                <div
                                                                                        data-testid="quiz-tier-chip"
                                                                                        data-tier={tier}
                                                                                        className={`mb-4 inline-block px-3 py-1.5 border font-mono text-[11px] tracking-[0.14em] uppercase ${chipClass}`}
                                                                                >
                                                                                        {chipText}
                                                                                </div>
                                                                        );
                                                                })()
                                                        )}
                                                        {!loadingResults && results && handSize && (
                                                                <div
                                                                        data-testid="quiz-hand-size-chip"
                                                                        className="mb-4 ml-2 inline-block px-3 py-1.5 border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.14em] uppercase"
                                                                >
                                                                        Fits: {handSize} hands
                                                                </div>
                                                        )}
                                                        {!loadingResults && results && results.results.length === 0 && (
                                                                <p
                                                                        data-testid={HOME.quizEmptyState}
                                                                        className="font-sans text-[15px] text-white/70"
                                                                >
                                                                        No exact match. Try widening one filter.
                                                                </p>
                                                        )}
                                                        {!loadingResults && results && results.results.length > 0 && (
                                                                <>
                                                                        <div
                                                                                data-testid={HOME.quizResultsGrid}
                                                                                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                                                                        >
                                                                                {results.results.map((r) => {
                                                                                        const isAdding = addingHandle === r.handle;
                                                                                        const isAdded = addedHandles.has(r.handle);
                                                                                        return (
                                                                                        <div
                                                                                                key={r.handle}
                                                                                                data-testid={HOME.quizResultCard}
                                                                                                className="border border-white/15 p-4 flex flex-col"
                                                                                        >
                                                                                                {r.image && (
                                                                                                        <div className="aspect-square bg-[#111] overflow-hidden mb-3">
                                                                                                                <img src={r.image} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
                                                                                                        </div>
                                                                                                )}
                                                                                                <p className="font-display text-[16px] leading-tight text-white uppercase">{r.title}</p>
                                                                                                <p className="mt-1 font-mono text-[12px] text-white/70">₹{Number(r.price || 0).toLocaleString("en-IN")}</p>
                                                                                                {r.match_count > 0 && (
                                                                                                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#FF9F1C]">
                                                                                                                ✓ {r.match_count} facets matched
                                                                                                        </p>
                                                                                                )}
                                                                                                <div className="mt-auto pt-3 flex flex-col gap-2">
                                                                                                        <button
                                                                                                                type="button"
                                                                                                                data-testid={`quiz-add-to-cart-${r.handle}`}
                                                                                                                onClick={() => handleAddSingleToCart(r)}
                                                                                                                disabled={isAdding || isAdded}
                                                                                                                className="touch-target inline-flex items-center justify-center w-full text-center bg-[#FF9F1C] text-black font-mono text-[10px] tracking-[0.18em] uppercase py-2 hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                                                                                style={{ minHeight: 44 }}
                                                                                                        >
                                                                                                                {isAdded ? "✓ Added" : isAdding ? "Adding…" : "Add to cart"}
                                                                                                        </button>
                                                                                                        <a
                                                                                                                href={`/p/${r.handle}`}
                                                                                                                data-testid={`quiz-view-product-${r.handle}`}
                                                                                                                className="touch-target inline-flex items-center justify-center w-full text-center border border-white/25 text-white font-mono text-[10px] tracking-[0.18em] uppercase py-2 hover:border-white transition-colors"
                                                                                                                style={{ minHeight: 44 }}
                                                                                                        >
                                                                                                                View product page
                                                                                                        </a>
                                                                                                </div>
                                                                                        </div>
                                                                                        );
                                                                                })}
                                                                        </div>

                                                                        {/* Trust microcopy strip */}
                                                                        <div
                                                                                data-testid="quiz-trust-strip"
                                                                                className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 font-mono text-[12px] md:text-[13px] lg:text-[14px] tracking-[0.06em] uppercase text-white/70"
                                                                        >
                                                                                <span>Free shipping over ₹1,499.</span>
                                                                                <span>7-day returns.</span>
                                                                                <span>18-month warranty.</span>
                                                                                <span>Razorpay UPI/Cards supported.</span>
                                                                        </div>

                                                                        {/* Status banners */}
                                                                        {addState === "partial" && addResult && (
                                                                                <div className="mt-6 p-4 border border-[#FF9F1C] bg-[#FF9F1C]/10 text-[#FF9F1C] font-mono text-[12px]">
                                                                                        <p className="uppercase tracking-[0.16em] font-bold">Partial success</p>
                                                                                        <p className="mt-2">Could not add: {addResult.failures.map((f) => f.title).join(", ")}</p>
                                                                                        <button
                                                                                                type="button"
                                                                                                onClick={handleAddFullLoadoutToCart}
                                                                                                className="touch-target mt-3 border border-[#FF9F1C] px-4 py-2 uppercase tracking-[0.16em]"
                                                                                                style={{ minHeight: 44 }}
                                                                                        >
                                                                                                Retry
                                                                                        </button>
                                                                                </div>
                                                                        )}
                                                                        {addState === "fail" && (
                                                                                <div className="mt-6 p-4 border border-red-500 bg-red-500/10 text-red-400 font-mono text-[12px]">
                                                                                        <p className="uppercase tracking-[0.16em] font-bold">Add failed</p>
                                                                                        <p className="mt-2">Could not add the loadout to your cart.</p>
                                                                                        <button
                                                                                                type="button"
                                                                                                onClick={handleAddFullLoadoutToCart}
                                                                                                className="touch-target mt-3 border border-red-400 px-4 py-2 uppercase tracking-[0.16em]"
                                                                                                style={{ minHeight: 44 }}
                                                                                        >
                                                                                                Retry
                                                                                        </button>
                                                                                        <p className="mt-3 text-white/60">
                                                                                                Need help? <a href={BUSINESS.whatsappUrl} className="underline">{BUSINESS.whatsapp.replace(/^\+91/, "+91 ")}</a>
                                                                                        </p>
                                                                                </div>
                                                                        )}
                                                                        {addState === "ok" && (
                                                                                <div className="mt-6 p-4 border border-emerald-500 bg-emerald-500/10 text-emerald-400 font-mono text-[12px]">
                                                                                        <p className="uppercase tracking-[0.16em] font-bold">✓ Added to cart</p>
                                                                                        <a href="#" onClick={(e) => { e.preventDefault(); cart.openDrawer(); }} className="mt-2 inline-block underline uppercase tracking-[0.16em]">Open Cart</a>
                                                                                </div>
                                                                        )}
                                                                </>
                                                        )}
                                                </div>
                                        )}
                                </div>

                                {/* Footer nav / sticky CTA */}
                                <div className="sticky-cta-bottom px-6 py-4 border-t border-white/10 flex items-center justify-between gap-4 bg-[#0A0A0A]">
                                        <button
                                                type="button"
                                                data-testid={HOME.quizRestart}
                                                onClick={restart}
                                                className="touch-target font-mono text-[11px] tracking-[0.18em] uppercase text-white/60 hover:text-white"
                                                style={{ minHeight: 44 }}
                                        >
                                                ↺ Restart
                                        </button>
                                        <div className="flex items-center gap-3">
                                                {stepIndex > 0 && !isResults && (
                                                        <button
                                                                type="button"
                                                                data-testid={HOME.quizBack}
                                                                onClick={() => setStepIndex(stepIndex - 1)}
                                                                className="touch-target font-mono text-[11px] tracking-[0.18em] uppercase text-white/70 border border-white/20 px-4 py-2 hover:border-white transition-colors"
                                                                style={{ minHeight: 44 }}
                                                        >
                                                                ← Back
                                                        </button>
                                                )}
                                                {!isResults && (
                                                        <button
                                                                type="button"
                                                                data-testid={HOME.quizNext}
                                                                disabled={!canGoNext()}
                                                                onClick={() => setStepIndex(stepIndex + 1)}
                                                                className="touch-target font-mono text-[11px] tracking-[0.18em] uppercase bg-[#FF9F1C] text-black px-4 py-2 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                                style={{ minHeight: 44 }}
                                                        >
                                                                {stepIndex === totalSteps - 2 ? "See results →" : "Next →"}
                                                        </button>
                                                )}
                                                {isResults && results && results.results.length > 0 && (
                                                        <>
                                                                <button
                                                                        type="button"
                                                                        data-testid={HOME.quizShareLoadout}
                                                                        onClick={handleShareLoadout}
                                                                        className="touch-target inline-flex items-center font-mono text-[11px] tracking-[0.18em] uppercase border border-white/25 text-white px-4 py-3 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors"
                                                                        style={{ minHeight: 48 }}
                                                                >
                                                                        ⧉ Share loadout
                                                                </button>
                                                                <a
                                                                        href="#"
                                                                        onClick={(e) => { e.preventDefault(); cart.openDrawer(); }}
                                                                        data-testid={HOME.quizAddAllToCart}
                                                                        className="touch-target inline-flex items-center font-mono text-[11px] tracking-[0.18em] uppercase border border-white/25 text-white px-4 py-3 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors"
                                                                        style={{ minHeight: 48 }}
                                                                >
                                                                        Open cart ➔
                                                                </a>
                                                        </>
                                                )}
                                                {shareToast}
                                        </div>
                                </div>
                        </div>
                </div>
        );
}
